import React, { useCallback, useEffect, useRef, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeatureBase,
  CharacterFeatureSource
} from "@dndModels";
import { PALETTE } from "@dndModels/Constants";

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import CharacterFeatureInput from "@dndComponents/character/features/CharacterFeatureInput";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import CharacterFeatureSourceInput from "@dndComponents/character/features/CharacterFeatureSourceInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props {
  obj: CharacterFeatureBase;
  source: CharacterFeatureSource;
  character: Character;
  show_source?: boolean;
  onSelect?: () => void;
  selected?: boolean;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterFeatureBaseInput({ 
  obj,
  source,
  character,
  show_source = false,
  onSelect,
  selected = false
}: Props) {
  const [show_source_modal, setShowSourceModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prev_selected, setPrevSelected] = useState(false);
  const [show_desc, setShowDesc] = useState(false);
  const [reloading, setReloading] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  useEffect(() => {
    character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
  }, [character, mounted, recalculated]);

  useEffect(() => {
    if (selected !== prev_selected) {
      setExpanded(selected);
      setPrevSelected(selected);
      setReloading(true);
    }
  }, [selected, prev_selected, setExpanded, setPrevSelected, setReloading]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useStyles = makeStyles()((theme) => {
    let colors = char_util.getColors({ char: character, user: api.user, for_id: source.source_id });
    
    let backgroundColor = colors.backgroundColor;
    let color = colors.color;
    
    if (backgroundColor === PALETTE.secondary.main && color === PALETTE.secondary.light) {
      const cfss = char_util.getCFSSForCF(character, obj);
      if (cfss.length > 1) {
        for (const cfs of cfss) {
          colors = char_util.getColors({ char: character, user: api.user, for_id: cfs.source_id });
          
          backgroundColor = colors.backgroundColor;
          color = colors.color;
          
          if (backgroundColor !== PALETTE.secondary.main || color !== PALETTE.secondary.light) {
            break;
          }
        }
      }
    }
    return ({
      root: {
        backgroundColor,
        border: "1px solid #777777",
        color,
        padding: "4px",
        margin: "4px",
        '&:hover': {
          backgroundColor: "Black",
          color: theme.palette.secondary.light
        },
      }
    });
  });

  const { classes, cx } = useStyles();

  const style: React.CSSProperties = {};
  if (expanded) {
    style.backgroundColor = "black";
    style.color = PALETTE.secondary.light;
  }
  if (obj.needs_attention) {
    style.border = "1px solid #1C9AEF";
  }
  return (
    <div className={cx(classes.root, obj.needs_attention ? "Flashing" : "")} style={style}>
      <Grid container spacing={0} direction="row">
        <Grid
          className="clickable"
          onClick={() => {
            if (onSelect) {
              // debugger;
              onSelect();
            } else {
              setExpanded(!expanded);
            }
          }}
          size={{
            xs: 12
          }}>
          { obj.feature_base?.name }{ show_source ? ` From ${char_util.getUseName(source)}` : "" }
        </Grid>
        { expanded && !onSelect &&
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            { obj.feature_base && 
              obj.feature_base.description !== "" &&
              (obj.features.length !== 1 || 
                !(obj.features[0].floating_feature && 
                  obj.features[0].floating_feature.description === obj.feature_base.description)) &&
              <Grid
                size={{
                  xs: 12
                }}>
                { obj.feature_base.description.length < 30 ?
                  <DisplayStringWithReplacements
                    display_string={obj.feature_base.description}
                  />
                :
                  <Grid container spacing={0} direction="column">
                    <Grid>
                      <ToggleButtonBox 
                        name={ show_desc ? "Hide Description" : "Show Description" }
                        value={show_desc}
                        onToggle={() => {
                          setShowDesc(!show_desc);
                        }}
                      />
                    </Grid>
                    { show_desc &&
                      <Grid>
                        <DisplayStringWithReplacements display_string={ obj.feature_base.description } />
                      </Grid>
                    }
                  </Grid>
                }
              </Grid>
            }
            { obj.features.map(feature => {
              if (feature.feature) {
                return (
                  <Grid
                    key={feature.true_id}
                    size={{
                      xs: 12
                    }}>
                    <CharacterFeatureInput 
                      obj={feature}
                      character={character}
                      source={source}
                      onChange={() => {
                        // setReloading(true);
                      }}
                    />
                  </Grid>
                );
              }
              return null;
            })}
            { show_source &&
              <Grid
                className="header clickable"
                onClick={() => {
                  setShowSourceModal(true);
                }}
                size={{
                  xs: 12
                }}>
                { char_util.getUseName(source) }
              </Grid>
            }
            <ModalOrDrawer
              open={show_source_modal}
              onClose={() => { setShowSourceModal(false) }}
            >
              <Grid container spacing={0} direction="column">
                <Grid className="centered header">
                  { char_util.getUseName(source) }
                </Grid>
                <Grid>
                  <CharacterFeatureSourceInput
                    character={character}
                    source={source}
                  />
                </Grid>
              </Grid>
            </ModalOrDrawer>
          </Grid>
        }
      </Grid>
    </div>
  );
}
